import { create } from 'zustand';
import { needsArchiveRepair } from '@/lib/consultation-archive-recovery';
import { importDocumentsToCanvas } from '@/features/chat/importDocumentsToCanvas';
import { produce } from 'immer';
import { persist, createJSONStorage } from 'zustand/middleware';
import { useAuthStore } from '@/stores/authStore';
import { getAuthScopeRevision } from '@/lib/auth-scoped-state-reset';
import { consultationUploadRecordId, putConsultationUploadRecord, updateConsultationUploadRecord,
  getConsultationUploadRecord, cancelConsultationUploadRecord, withConsultationUploadLease,
  type ConsultationUploadRecord } from '@/lib/consultation-upload-journal';
import type { ConsultationDocument, ConsultationRole, ReferenceFeedbackDraft, StyleCandidateStatus } from '@/types/consultation';
import { importConsultationAsset, importConsultationLibrary, uploadConsultationDocument, type ConsultationLibrarySource, type ConsultationUploadRecovery, type ConsultationUploadProgress } from '@/lib/api/consultation';

export interface ConsultationAssetReference {
  assetId: string;
  previewUrl: string;
  label: string;
  status: 'preparing' | 'ready' | 'failed';
  documentId?: string;
  errorCode?: string;
  attemptId?: number;
  librarySource?: ConsultationLibrarySource;
  expectedCount?: number;
}

export interface ConsultationLocalUpload {
  id: string;
  filename: string;
  mediaType: 'image' | 'video' | 'document';
  previewUrl?: string;
  status: 'preparing' | 'failed';
  errorCode?: string;
  progress?: Omit<ConsultationUploadProgress, 'document'>;
  recoveryId?: string;
}

export interface ConsultationDraft {
  uploadReceipts?: Record<string, string>;
  uploads?: ConsultationLocalUpload[];
  role: ConsultationRole | null;
  documents: ConsultationDocument[];
  references?: ConsultationAssetReference[];
  attachmentOrder?: string[];
}

export interface CampaignActionDraft { candidateTitle?: string; candidatePreviewUrl?: string | null; feedback?: string; action: import('@/types/consultation').CampaignAction; revision: number }

interface ConsultationStore {
  handoffDraftVersions: Record<string, number>;
  proposalReferenceDrafts: Record<string, import('@/types/consultation').VisualProposalReferenceDraft>;
  proposalReferenceDraftVersions: Record<string, number>;
  setProposalReference: (projectId: string, threadId: string, draft: import('@/types/consultation').VisualProposalReferenceDraft | null) => void;
  styleCandidateStatuses: Record<string, StyleCandidateStatus>;
  setStyleCandidateStatus: (projectId: string, threadId: string, status: StyleCandidateStatus) => void;
  updateReferenceSelection: (projectId: string, threadId: string, draft: ReferenceFeedbackDraft, managedCandidateIds: string[]) => boolean;
  updateHandoffText: (projectId: string, threadId: string, text: string) => void;
  campaignActionDrafts: Record<string, CampaignActionDraft>;
  setCampaignAction: (projectId: string, threadId: string, draft: CampaignActionDraft | null) => void;
  referenceFeedbackDrafts: Record<string, import('@/types/consultation').ReferenceFeedbackDraft>;
  setReferenceFeedback: (projectId: string, threadId: string, draft: import('@/types/consultation').ReferenceFeedbackDraft | null) => void;
  drafts: Record<string, ConsultationDraft>;
  uploadFiles: (projectId: string, files: File[], threadId?: string | null) => Promise<void>;
  restoreUploads: (projectId: string, ownerId: string, threadId: string | null, isActive?: () => boolean) => Promise<void>;
  retryUpload: (projectId: string, id: string) => Promise<void>;
  removeUpload: (projectId: string, id: string) => void;
  pendingUploads: Record<string, number>;
  draftVersions: Record<string, number>;
  getDraftVersion: (projectId: string) => number;
  replaceDraft: (projectId: string, draft?: ConsultationDraft) => void;
  resetAll: () => void;
  consumeDocuments: (projectId: string, ids: string[], version: number) => void;
  tryBeginUploads: (projectId: string, count: number) => boolean;
  finishUploads: (projectId: string, count: number, version?: number) => void;
  setRole: (projectId: string, role: ConsultationRole | null) => void;
  addDocument: (projectId: string, document: ConsultationDocument, version?: number) => void;
  refreshDocument: (projectId: string, document: ConsultationDocument) => void;
  removeDocument: (projectId: string, documentId: string) => void;
  prepareAssetReference: (projectId: string, reference: Pick<ConsultationAssetReference, 'assetId' | 'previewUrl' | 'label' | 'librarySource' | 'expectedCount'>) => Promise<'ready' | 'failed' | 'duplicate' | 'limit' | 'stale'>;
  removeAssetReference: (projectId: string, assetId: string) => void;
}

function syncAttachmentOrder(draft: ConsultationDraft) {
  const linkedIds = new Set((draft.references ?? []).map(reference => reference.documentId));
  const keys = [
    ...(draft.uploads ?? []).map(upload => `upload:${upload.id}`),
    ...(draft.references ?? []).map(reference => `asset:${reference.assetId}`),
    ...draft.documents.filter(document => !linkedIds.has(document.document_id)).map(document => `document:${document.document_id}`),
  ];
  const liveKeys = new Set(keys);
  draft.attachmentOrder = [...new Set([...(draft.attachmentOrder ?? []), ...keys])].filter(key => liveKeys.has(key));
}

const localFiles = new Map<string, {file: File; recovery: ConsultationUploadRecovery;
  record?: ConsultationUploadRecord; authRevision?: number}>();
function retireUploadRecord(id?: string) {
  // Retired draft identities are never recovered by scanning the journal. A
  // failed cleanup cannot reattach a consumed file; the TTL bounds its bytes.
  if (id) void cancelConsultationUploadRecord(id).catch(() => {});
}
function uploadCheckpoint(recovery: ConsultationUploadRecovery) {
  const value = recovery.prepared ?? recovery.intent;
  return value ? {fingerprint: value.fingerprint, byteSize: value.byteSize,
    uploadId: recovery.intent?.uploadId, ...(recovery.prepared ? {
      documentId: recovery.prepared.documentId, putState: recovery.prepared.putState,
    } : {})} : null;
}

function recoveredUpload(record: ConsultationUploadRecord, file: File, authRevision: number): ConsultationUploadRecovery {
  const checkpoint = record.checkpoint;
  if (!checkpoint) return {};
  const common = {file, projectId: record.projectId, ownerId: record.ownerId, authRevision,
    fingerprint: checkpoint.fingerprint, byteSize: checkpoint.byteSize};
  return {
    ...(checkpoint.uploadId ? {intent: {...common, uploadId: checkpoint.uploadId}} : {}),
    ...(checkpoint.documentId ? {prepared: {...common, documentId: checkpoint.documentId,
      putState: checkpoint.putState ?? 'unknown'}} : {}), resumeSameIdentity: true,
  };
}

/** Old partial receipts are usable only with their unchanged owned original. */
async function verifyArchiveRepair(record: ConsultationUploadRecord, file: File): Promise<void> {
  const document = record.readyDocument;
  const {hashUploadFile} = await import('@/lib/api/upload-content-hash');
  const fingerprint = await hashUploadFile(file);
  if (!document || !/\.(zip|rar)$/i.test(file.name) || document.filename !== file.name
    || document.fingerprint !== fingerprint
    || record.checkpoint && (record.checkpoint.fingerprint !== fingerprint || record.checkpoint.byteSize !== file.size)) {
    throw Object.assign(new Error('document_version_mismatch'), {code: 'document_version_mismatch'});
  }
}

function releaseLocalFile(id: string) {
  const entry = localFiles.get(id);
  if (entry) delete entry.recovery.prepared;
  localFiles.delete(id);
}
const localPreviewUrls = new Set<string>();
function localPreview(file: File) {
  const url = URL.createObjectURL(file);
  localPreviewUrls.add(url);
  return url;
}
function releaseLocalPreview(url?: string) {
  if (url && localPreviewUrls.delete(url)) URL.revokeObjectURL(url);
}
const uploadControllers = new Map<string, AbortController>();
let nextAttempt = 0;

async function runUpload(projectId: string, id: string) {
  const store = useConsultationStore;
  const entry = localFiles.get(id);
  if (!entry) return;
  const {file, recovery} = entry;
  const version = store.getState().getDraftVersion(projectId);
  const controller = new AbortController();
  uploadControllers.set(id, controller);
  const isCurrent = () => store.getState().getDraftVersion(projectId) === version
    && uploadControllers.get(id) === controller && !controller.signal.aborted
    && (!entry.record || useAuthStore.getState().user?.id === entry.record.ownerId
      && getAuthScopeRevision() === entry.authRevision)
    && store.getState().drafts[projectId]?.uploads?.some(upload => upload.id === id);
  store.setState(produce((state: ConsultationStore) => {
    const upload = state.drafts[projectId]?.uploads?.find(upload => upload.id === id);
    if (upload) { upload.status = 'preparing'; upload.errorCode = undefined; upload.progress = undefined; }
  }));
  const perform = async () => {
    try {
      if (!isCurrent()) return;
      let readyDocument: ConsultationDocument | undefined;
      if (entry.record) {
        // Durably save the original before hashing/reserving/uploading anything.
        await putConsultationUploadRecord(entry.record);
        if (!isCurrent()) return;
        // Another tab may have completed while this attempt waited for its lease.
        // Read the latest receipt/checkpoint only after taking exclusive ownership.
        const latest = await getConsultationUploadRecord(entry.record.id);
        if (!isCurrent()) return;
        if (!latest) throw new DOMException('Upload retired', 'AbortError');
        readyDocument = latest.readyDocument;
        if (needsArchiveRepair(readyDocument)) {
          await verifyArchiveRepair(latest, file);
          if (!isCurrent()) return;
          readyDocument = undefined;
        }
        delete recovery.intent;
        delete recovery.prepared;
        delete recovery.resumeSameIdentity;
        Object.assign(recovery, recoveredUpload(latest, file, entry.authRevision!));
      }
      const document = readyDocument ?? await uploadConsultationDocument(projectId, file, controller.signal, recovery, progress => {
        if (!isCurrent()) return;
        store.setState(produce((state: ConsultationStore) => {
          const upload = state.drafts[projectId]?.uploads?.find(upload => upload.id === id);
          if (upload?.status !== 'preparing') return;
          // Only transport progress belongs to the pending card. The returned
          // document enters the owned ready draft through the success path below.
          upload.progress = {stage: progress.stage === 'ready' ? 'organizing' : progress.stage, bytesUploaded: progress.bytesUploaded,
            bytesTotal: progress.bytesTotal};
        }));
      }, entry.record ? async () => {
        if (!isCurrent()) throw new DOMException('Upload retired', 'AbortError');
        await updateConsultationUploadRecord(entry.record!.id, {checkpoint: uploadCheckpoint(recovery)});
        if (!isCurrent()) throw new DOMException('Upload retired', 'AbortError');
      } : undefined);
      if (!isCurrent()) return;
      if (entry.record) {
        await updateConsultationUploadRecord(entry.record.id, {readyDocument: document});
        if (!isCurrent()) return;
      }
      try {
        await importDocumentsToCanvas(projectId, document, controller.signal, progress => {
          if (!isCurrent()) return;
          store.setState(produce((state: ConsultationStore) => {
            const upload = state.drafts[projectId]?.uploads?.find(item => item.id === id);
            if (upload?.status === 'preparing') upload.progress = {stage: 'organizing', ...progress};
          }));
        });
      } catch (cause) {
        throw Object.assign(new Error('canvas_import_failed', {cause}), {code: 'canvas_import_failed'});
      }
      if (!isCurrent()) return;
      store.setState(produce((state: ConsultationStore) => {
        const draft = state.drafts[projectId];
        const upload = draft.uploads!.find(upload => upload.id === id)!;
        if (!draft.documents.some(item => item.document_id === document.document_id)) {
          draft.documents.push({...document, preview_url: upload.previewUrl || document.preview_url});
        }
        if (entry.record) (draft.uploadReceipts ??= {})[document.document_id] = entry.record.id;
        draft.attachmentOrder = draft.attachmentOrder?.map(key => key === `upload:${id}` ? `document:${document.document_id}` : key);
        draft.uploads = draft.uploads!.filter(upload => upload.id !== id);
      }));
      releaseLocalFile(id);
    } catch (error) {
      if (isCurrent()) store.setState(produce((state: ConsultationStore) => {
        const upload = state.drafts[projectId].uploads!.find(upload => upload.id === id)!;
        upload.status = 'failed';
        upload.errorCode = error instanceof Error && 'code' in error && typeof error.code === 'string'
          ? error.code : 'reference_prepare_failed';
      }));
    }
  };
  try {
    if (entry.record) await withConsultationUploadLease(entry.record.id, perform, false, controller.signal);
    else await perform();
  } catch (error) {
    if (isCurrent()) store.setState(produce((state: ConsultationStore) => {
      const upload = state.drafts[projectId]?.uploads?.find(item => item.id === id);
      if (upload) { upload.status = 'failed'; upload.errorCode = 'upload_recovery_unavailable'; }
    }));
  } finally {
    if (uploadControllers.get(id) === controller) uploadControllers.delete(id);
    store.getState().finishUploads(projectId, 1, version);
  }
}

export function canRetryConsultationUpload(id: string) { return localFiles.has(id); }

const imports = new Map<number, { projectId: string; controller: AbortController }>();

/** Candidate IDs are server-owned image identities; direction membership is not a new image. */
function uniqueReferences(references: ReferenceFeedbackDraft['references']) {
  return Array.from(new Map(references.map(reference => [reference.candidate_id, reference])).values());
}

/** Keep only the in-memory user selection and text. */
function projectReferenceDraft(draft: ReferenceFeedbackDraft): ReferenceFeedbackDraft {
  return {artifact_id: draft.artifact_id, version: draft.version,
    ...(draft.feedback !== undefined ? {feedback: draft.feedback} : {}),
    references: uniqueReferences(draft.references)};
}

/** Replace only the visible board's selection, preserving other directions in the same proposal. */
export function mergeReferenceSelection(previous: ReferenceFeedbackDraft | undefined, draft: ReferenceFeedbackDraft, managedCandidateIds: string[]): ReferenceFeedbackDraft | null {
  const sameProposal = previous?.artifact_id === draft.artifact_id && previous.version === draft.version;
  const managed = new Set(managedCandidateIds);
  const references = uniqueReferences([
    ...(sameProposal ? previous.references.filter(reference => !managed.has(reference.candidate_id)) : []),
    ...draft.references.filter(reference => managed.has(reference.candidate_id)),
  ]);
  return references.length ? projectReferenceDraft({artifact_id: draft.artifact_id, version: draft.version,
    feedback: draft.feedback ?? (sameProposal ? previous.feedback : undefined), references}) : null;
}

// 仅当前 composer 的临时素材选择；切换/新建对话时必须清空。
// brief 权威始终来自服务器的 thread-scoped snapshot。
export const useConsultationStore = create<ConsultationStore>()(persist((set, get) => ({
  handoffDraftVersions: {},
  proposalReferenceDrafts: {},
  proposalReferenceDraftVersions: {},
  setProposalReference: (projectId, threadId, draft) => set(produce((state: ConsultationStore) => {
    const key = JSON.stringify([projectId, threadId]);
    const current = state.proposalReferenceDrafts[key];
    if (current?.artifact_id === draft?.artifact_id && current?.version === draft?.version
      && current?.title === draft?.title) return;
    state.proposalReferenceDraftVersions[key] = (state.proposalReferenceDraftVersions[key] ?? 0) + 1;
    if (draft) state.proposalReferenceDrafts[key] = {artifact_id: draft.artifact_id, version: draft.version, title: draft.title};
    else delete state.proposalReferenceDrafts[key];
  })),
  styleCandidateStatuses: {},
  setStyleCandidateStatus: (projectId, threadId, status) => set(produce((state: ConsultationStore) => {
    state.styleCandidateStatuses[JSON.stringify([projectId, threadId, status.artifact_id, status.version])] = status;
  })),
  updateReferenceSelection: (projectId, threadId, draft, managedCandidateIds) => {
    const key = JSON.stringify([projectId, threadId]);
    const previous = get().referenceFeedbackDrafts[key];
    // A delayed callback from an older revision cannot replace a newer selection.
    if (previous?.artifact_id === draft.artifact_id && previous.version > draft.version) return false;
    const next = mergeReferenceSelection(previous, draft, managedCandidateIds);
    set(produce((state: ConsultationStore) => {
      state.handoffDraftVersions[key] = (state.handoffDraftVersions[key] ?? 0) + 1;
      if (next) { state.referenceFeedbackDrafts[key] = next; delete state.campaignActionDrafts[key]; }
      else delete state.referenceFeedbackDrafts[key];
    }));
    return true;
  },
  updateHandoffText: (projectId, threadId, text) => set(produce((state: ConsultationStore) => {
    const key = JSON.stringify([projectId, threadId]);
    state.handoffDraftVersions[key] = (state.handoffDraftVersions[key] ?? 0) + 1;
    const reference = state.referenceFeedbackDrafts[key];
    const campaign = state.campaignActionDrafts[key];
    if (reference) reference.feedback = text;
    if (campaign) campaign.feedback = text;
  })),
  campaignActionDrafts: {},
  setCampaignAction: (projectId, threadId, draft) => set(produce((state: ConsultationStore) => {
    const key = JSON.stringify([projectId, threadId]);
    state.handoffDraftVersions[key] = (state.handoffDraftVersions[key] ?? 0) + 1;
    if (draft) { state.campaignActionDrafts[key] = draft; delete state.referenceFeedbackDrafts[key]; }
    else delete state.campaignActionDrafts[key];
  })),
  referenceFeedbackDrafts: {},
  setReferenceFeedback: (projectId, threadId, draft) => set(produce((state: ConsultationStore) => {
    const key = JSON.stringify([projectId, threadId]);
    state.handoffDraftVersions[key] = (state.handoffDraftVersions[key] ?? 0) + 1;
    if (draft) { state.referenceFeedbackDrafts[key] = projectReferenceDraft(draft); delete state.campaignActionDrafts[key]; }
    else delete state.referenceFeedbackDrafts[key];
  })),
  drafts: {},
  uploadFiles: async (projectId, files, threadId = null) => {
    if (!get().tryBeginUploads(projectId, files.length)) return;
    const uploads = files.map(file => {
      const id = crypto.randomUUID();
      const ownerId = useAuthStore.getState().user?.id;
      const durable = /\.(pptx?|zip|rar)$/i.test(file.name);
      const now = Date.now();
      const record: ConsultationUploadRecord | undefined = durable && ownerId ? {
        id: consultationUploadRecordId(ownerId, projectId, threadId, id),
        ownerId, projectId, threadId, uploadId: id, file, fileName: file.name,
        lastModified: file.lastModified, createdAt: now, updatedAt: now,
      } : undefined;
      localFiles.set(id, {file, recovery: {}, record, authRevision: getAuthScopeRevision()});
      const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
      const mediaType = ['png', 'jpg', 'jpeg', 'webp', 'heic', 'heif'].includes(ext) ? 'image' as const
        : ['mp4', 'mov', 'webm', 'm4v'].includes(ext) ? 'video' as const : 'document' as const;
      return {id, filename: file.name, mediaType, status: 'preparing' as const, recoveryId: record?.id,
        previewUrl: mediaType !== 'document' && !['heic', 'heif'].includes(ext) ? localPreview(file) : undefined};
    });
    set(produce((state: ConsultationStore) => {
      const draft = state.drafts[projectId] ??= {role: null, documents: []};
      syncAttachmentOrder(draft);
      draft.uploads = [...(draft.uploads ?? []), ...uploads];
      draft.attachmentOrder!.push(...uploads.map(upload => `upload:${upload.id}`));
    }));
    await Promise.all(uploads.map(upload => runUpload(projectId, upload.id)));
  },
  restoreUploads: async (projectId, ownerId, threadId, isActive = () => true) => {
    const version = get().getDraftVersion(projectId);
    const authRevision = getAuthScopeRevision();
    const current = () => isActive() && get().getDraftVersion(projectId) === version
      && useAuthStore.getState().user?.id === ownerId && getAuthScopeRevision() === authRevision;
    // Older clients admitted partial archives as ready. Reopen only the exact
    // receipt still attached to this draft; never scan/revive consumed files.
    for (const document of get().drafts[projectId]?.documents ?? []) {
      if (!current()) return;
      if (!needsArchiveRepair(document)) continue;
      const receiptId = get().drafts[projectId]?.uploadReceipts?.[document.document_id];
      if (!receiptId) continue;
      const record = await getConsultationUploadRecord(receiptId);
      if (!current()) return;
      if (!record || record.cancelled || record.ownerId !== ownerId || record.projectId !== projectId
        || record.threadId !== threadId || record.readyDocument?.document_id !== document.document_id
        || record.readyDocument?.fingerprint !== document.fingerprint || !needsArchiveRepair(record.readyDocument)) continue;
      const file = new File([record.file], record.fileName, {type: record.file.type, lastModified: record.lastModified});
      try { await verifyArchiveRepair(record, file); } catch { continue; }
      if (!current()) return;
      set(produce((state: ConsultationStore) => {
        const draft = state.drafts[projectId];
        if (draft?.uploadReceipts?.[document.document_id] !== receiptId
          || !draft.documents.some(item => item.document_id === document.document_id && item.fingerprint === document.fingerprint)) return;
        draft.documents = draft.documents.filter(item => item.document_id !== document.document_id);
        delete draft.uploadReceipts[document.document_id];
        if (!draft.uploads?.some(item => item.id === record.uploadId)) {
          (draft.uploads ??= []).push({id: record.uploadId, filename: record.fileName, mediaType: 'document',
            status: 'failed', errorCode: 'archive_members_storage_unavailable', recoveryId: record.id});
        }
        draft.attachmentOrder = draft.attachmentOrder?.map(key => key === `document:${document.document_id}` ? `upload:${record.uploadId}` : key);
      }));
    }
    const uploads = get().drafts[projectId]?.uploads ?? [];
    for (const upload of uploads) {
      if (!current()) return;
      if (!upload.recoveryId || localFiles.has(upload.id)) continue;
      const record = await getConsultationUploadRecord(upload.recoveryId);
      if (!current()) return;
      if (!get().drafts[projectId]?.uploads?.some(item => item.id === upload.id && item.recoveryId === upload.recoveryId)) continue;
      if (!record || record.cancelled || record.ownerId !== ownerId || record.projectId !== projectId
          || record.threadId !== threadId || record.uploadId !== upload.id) continue;
      let canvasReady = false;
      const repair = needsArchiveRepair(record.readyDocument);
      if (record.readyDocument && !repair) {
        try {
          await importDocumentsToCanvas(projectId, record.readyDocument);
          canvasReady = true;
        } catch { /* Keep the ready receipt and expose the same recoverable upload. */ }
        if (!current()) return;
      }
      if (record.readyDocument && canvasReady) {
        set(produce((state: ConsultationStore) => {
          const draft = state.drafts[projectId];
          if (!draft?.uploads?.some(item => item.id === upload.id)) return;
          const document = record.readyDocument!;
          if (!draft.documents.some(item => item.document_id === document.document_id)) draft.documents.push(document);
          (draft.uploadReceipts ??= {})[document.document_id] = record.id;
          draft.uploads = draft.uploads.filter(item => item.id !== upload.id);
          draft.attachmentOrder = draft.attachmentOrder?.map(key => key === `upload:${upload.id}` ? `document:${document.document_id}` : key);
        }));
        continue;
      }
      const file = new File([record.file], record.fileName, {type: record.file.type, lastModified: record.lastModified});
      const recovery = recoveredUpload(record, file, authRevision);
      localFiles.set(upload.id, {file, recovery, record, authRevision});
      set(produce((state: ConsultationStore) => {
        const item = state.drafts[projectId]?.uploads?.find(item => item.id === upload.id);
        if (item) { item.status = 'failed'; item.errorCode = repair ? 'archive_members_storage_unavailable' : 'upload_recovery_ready'; item.progress = undefined; }
      }));
    }
  },
  retryUpload: async (projectId, id) => {
    const upload = get().drafts[projectId]?.uploads?.find(upload => upload.id === id);
    if (!upload || upload.status !== 'failed' || !localFiles.has(id) || get().pendingUploads[projectId]) return;
    set(produce((state: ConsultationStore) => { state.pendingUploads[projectId] = 1; }));
    await runUpload(projectId, id);
  },
  removeUpload: (projectId, id) => {
    uploadControllers.get(id)?.abort();
    retireUploadRecord(localFiles.get(id)?.record?.id ?? get().drafts[projectId]?.uploads?.find(upload => upload.id === id)?.recoveryId);
    releaseLocalFile(id);
    const upload = get().drafts[projectId]?.uploads?.find(upload => upload.id === id);
    releaseLocalPreview(upload?.previewUrl);
    set(produce((state: ConsultationStore) => {
      const draft = state.drafts[projectId];
      if (!draft) return;
      draft.uploads = draft.uploads?.filter(upload => upload.id !== id);
      syncAttachmentOrder(draft);
    }));
  },
  pendingUploads: {},
  draftVersions: {},
  getDraftVersion: projectId => get().draftVersions[projectId] ?? 0,
  replaceDraft: (projectId, draft) => {
    const retained = new Set(draft?.documents.map(document => document.preview_url));
    for (const document of get().drafts[projectId]?.documents ?? []) {
      if (!retained.has(document.preview_url)) releaseLocalPreview(document.preview_url);
    }
    for (const upload of get().drafts[projectId]?.uploads ?? []) {
      uploadControllers.get(upload.id)?.abort();
      releaseLocalPreview(upload.previewUrl);
      releaseLocalFile(upload.id);
    }
    for (const operation of imports.values()) {
      if (operation.projectId === projectId) operation.controller.abort();
    }
    set(produce((state: ConsultationStore) => {
      state.draftVersions[projectId] = (state.draftVersions[projectId] ?? 0) + 1;
      delete state.pendingUploads[projectId];
      if (draft) {
        state.drafts[projectId] = structuredClone(draft);
        for (const upload of state.drafts[projectId].uploads ?? []) upload.status = 'failed';
        // 回滚会话切换时，旧请求已失效；不能恢复一个永不结束的准备状态。
        for (const reference of state.drafts[projectId].references ?? []) {
          if (reference.status === 'preparing') {
            reference.status = 'failed';
            reference.errorCode = 'reference_prepare_interrupted';
          }
        }
      } else delete state.drafts[projectId];
    }));
  },
  resetAll: () => {
    uploadControllers.forEach(controller => controller.abort());
    for (const id of localFiles.keys()) releaseLocalFile(id);
    localPreviewUrls.forEach(url => URL.revokeObjectURL(url));
    localPreviewUrls.clear();
    try {
      Object.keys(sessionStorage).filter(key => key.startsWith('composer-draft-v2:')).forEach(key => sessionStorage.removeItem(key));
    } catch { /* Browser storage may be unavailable. */ }
    for (const projectId of new Set([...Object.keys(get().drafts), ...Object.keys(get().pendingUploads)])) {
      get().replaceDraft(projectId);
    }
    set(produce((state: ConsultationStore) => {
      for (const key of Object.keys(state.handoffDraftVersions)) state.handoffDraftVersions[key] += 1;
      state.referenceFeedbackDrafts = {}; state.campaignActionDrafts = {}; state.styleCandidateStatuses = {};
      for (const key of Object.keys(state.proposalReferenceDraftVersions)) state.proposalReferenceDraftVersions[key] += 1;
      state.proposalReferenceDrafts = {};
    }));
  },
  consumeDocuments: (projectId, ids, version) => {
    if (get().getDraftVersion(projectId) !== version || !ids.length) return;
    const selected = new Set(ids);
    for (const id of ids) retireUploadRecord(get().drafts[projectId]?.uploadReceipts?.[id]);
    set(produce((state: ConsultationStore) => {
      const draft = state.drafts[projectId];
      if (!draft) return;
      draft.documents = draft.documents.filter(document => !selected.has(document.document_id));
      for (const id of ids) if (draft.uploadReceipts) delete draft.uploadReceipts[id];
      draft.references = draft.references?.filter(reference => !reference.documentId || !selected.has(reference.documentId));
      syncAttachmentOrder(draft);
    }));
  },
  tryBeginUploads: (projectId, count) => {
    if (!Number.isInteger(count) || count <= 0 || (get().pendingUploads[projectId] ?? 0) > 0) return false;
    set(produce((state: ConsultationStore) => { state.pendingUploads[projectId] = count; }));
    return true;
  },
  finishUploads: (projectId, count, version) => {
    if (version !== undefined && get().getDraftVersion(projectId) !== version) return;
    if (!Number.isInteger(count) || count <= 0) return;
    set(produce((state: ConsultationStore) => {
      const remaining = Math.max(0, (state.pendingUploads[projectId] ?? 0) - count);
      if (remaining) state.pendingUploads[projectId] = remaining;
      else delete state.pendingUploads[projectId];
    }));
  },
  setRole: (projectId, role) => set(produce((state: ConsultationStore) => {
    state.drafts[projectId] ??= { role: null, documents: [] };
    state.drafts[projectId].role = role;
  })),
  addDocument: (projectId, document, version) => {
    if (version !== undefined && get().getDraftVersion(projectId) !== version) return;
    set(produce((state: ConsultationStore) => {
      state.drafts[projectId] ??= { role: null, documents: [] };
      if (!state.drafts[projectId].documents.some(item => item.document_id === document.document_id)) {
        syncAttachmentOrder(state.drafts[projectId]);
        state.drafts[projectId].documents.push(document);
        state.drafts[projectId].attachmentOrder!.push(`document:${document.document_id}`);
      }
    }));
  },
  refreshDocument: (projectId, document) => set(produce((state: ConsultationStore) => {
    const current = state.drafts[projectId]?.documents.find(item => item.document_id === document.document_id);
    if (current) {
      const preview = current.preview_url?.startsWith('blob:') && current.fingerprint === document.fingerprint
        ? current.preview_url : undefined;
      Object.assign(current, document);
      if (preview) current.preview_url = preview;
    }
  })),
  removeDocument: (projectId, documentId) => set(produce((state: ConsultationStore) => {
    const draft = state.drafts[projectId];
    if (draft) {
      releaseLocalPreview(draft.documents.find(document => document.document_id === documentId)?.preview_url);
      retireUploadRecord(draft.uploadReceipts?.[documentId]);
      if (draft.uploadReceipts) delete draft.uploadReceipts[documentId];
      draft.documents = draft.documents.filter(item => item.document_id !== documentId);
      draft.references = draft.references?.filter(item => item.documentId !== documentId);
      syncAttachmentOrder(draft);
    }
  })),
  prepareAssetReference: async (projectId, reference) => {
    const draft = get().drafts[projectId];
    const existing = draft?.references?.find(item => item.assetId === reference.assetId);
    if (existing && existing.status !== 'failed') return 'duplicate';
    const count = reference.librarySource ? reference.expectedCount ?? 1 : 1;
    if (!Number.isSafeInteger(count) || count < 1) return 'limit';
    const version = get().getDraftVersion(projectId);
    const attemptId = ++nextAttempt;
    const controller = new AbortController();
    imports.set(attemptId, { projectId, controller });
    const isCurrent = () => get().getDraftVersion(projectId) === version
      && get().drafts[projectId]?.references?.some(item => item.attemptId === attemptId);
    set(produce((state: ConsultationStore) => {
      state.drafts[projectId] ??= { role: null, documents: [] };
      const current = state.drafts[projectId];
      syncAttachmentOrder(current);
      current.references ??= [];
      const retry = current.references.find(item => item.assetId === reference.assetId);
      if (retry) Object.assign(retry, { status: 'preparing', errorCode: undefined, attemptId });
      else {
        current.references.push({ ...reference, status: 'preparing', attemptId });
        current.attachmentOrder!.push(`asset:${reference.assetId}`);
      }
      state.pendingUploads[projectId] = (state.pendingUploads[projectId] ?? 0) + count;
    }));
    try {
      const result = reference.librarySource
        ? await importConsultationLibrary(projectId, reference.librarySource, count, controller.signal)
        : {documents: [await importConsultationAsset(projectId, reference.assetId, controller.signal)]};
      if (!isCurrent()) return 'stale';
      const documents = result.documents;
      if (!documents.length || documents.length > count || documents.some(document => !document.document_id
        || (!reference.librarySource && document.source_asset_id !== reference.assetId))) throw new Error('invalid_asset_document');
      set(produce((state: ConsultationStore) => {
        const current = state.drafts[projectId];
        const item = current.references!.find(ref => ref.attemptId === attemptId)!;
        const ready = documents.map((document, index) => ({...item, status: 'ready' as const,
          assetId: index === 0 ? reference.assetId : `${reference.assetId}:${index}`,
          // A shared cover is not a poster for every anchor in a photo bank.
          previewUrl: index === 0 ? item.previewUrl : document.preview_url || '',
          documentId: document.document_id}));
        current.references!.splice(current.references!.indexOf(item), 1, ...ready);
        current.attachmentOrder = current.attachmentOrder?.flatMap(key => key === `asset:${reference.assetId}`
          ? ready.map(ref => `asset:${ref.assetId}`) : [key]);
        for (const document of documents) {
          if (!current.documents.some(doc => doc.document_id === document.document_id)) current.documents.push(document);
        }
      }));
      return 'ready';
    } catch (error) {
      if (!isCurrent()) return 'stale';
      set(produce((state: ConsultationStore) => {
        const item = state.drafts[projectId].references!.find(ref => ref.attemptId === attemptId)!;
        item.status = 'failed';
        item.errorCode = error instanceof Error && 'code' in error && typeof error.code === 'string'
          ? error.code : 'reference_prepare_failed';
      }));
      return 'failed';
    } finally {
      imports.delete(attemptId);
      get().finishUploads(projectId, count, version);
    }
  },
  removeAssetReference: (projectId, assetId) => {
    const item = get().drafts[projectId]?.references?.find(ref => ref.assetId === assetId);
    if (item?.attemptId) imports.get(item.attemptId)?.controller.abort();
    set(produce((state: ConsultationStore) => {
      const draft = state.drafts[projectId];
      if (!draft || !item) return;
      draft.references = draft.references?.filter(ref => ref.assetId !== assetId);
      // 同内容不同资产可命中同一资料；另一条引用仍使用时保留发送身份。
      if (item.documentId && !draft.references?.some(ref => ref.documentId === item.documentId)) {
        draft.documents = draft.documents.filter(doc => doc.document_id !== item.documentId);
      }
      syncAttachmentOrder(draft);
    }));
  },
}), {
  name: 'campaign-reference-feedback-v1',
  storage: createJSONStorage(() => sessionStorage),
  merge: (persisted, current) => {
    const saved = persisted as Partial<Pick<ConsultationStore, 'campaignActionDrafts' | 'proposalReferenceDrafts'>> | undefined;
    return {...current,
      campaignActionDrafts: saved?.campaignActionDrafts ?? current.campaignActionDrafts,
      proposalReferenceDrafts: saved?.proposalReferenceDrafts ?? current.proposalReferenceDrafts,
      // Older clients saved reference drafts here. Never hydrate those choices.
      referenceFeedbackDrafts: current.referenceFeedbackDrafts,
    };
  },
  partialize: state => ({ campaignActionDrafts: state.campaignActionDrafts, proposalReferenceDrafts: state.proposalReferenceDrafts }),
}));

// 本地消息完成快照后即移交资料，不等待 HTTP/SSE 确认。
// 拒绝时仅恢复仍未被用户编辑、切换或替换的草稿。
export function createConsultationSubmission(projectId: string, documentIds: string[]) {
  const version = useConsultationStore.getState().getDraftVersion(projectId);
  let consumed = false;
  let rejected = false;
  let before: ConsultationDraft | undefined;
  let after: ConsultationDraft | undefined;
  const consume = () => {
    if (consumed || rejected || useConsultationStore.getState().getDraftVersion(projectId) !== version) return;
    consumed = true;
    before = useConsultationStore.getState().drafts[projectId];
    useConsultationStore.getState().consumeDocuments(projectId, documentIds, version);
    after = useConsultationStore.getState().drafts[projectId];
  };
  return {
    start: consume,
    accept: consume,
    reject() {
      rejected = true;
      const current = useConsultationStore.getState();
      if (!consumed || !before || current.getDraftVersion(projectId) !== version
        || current.drafts[projectId] !== after) return;
      useConsultationStore.setState(produce((state: ConsultationStore) => {
        state.drafts[projectId] = before!;
      }));
    },
  };
}
